import { FC } from "react";
import { ChildCard, ComingSoonCard } from "@/components/molecules/ChildCard";
import { NavNode } from "@/app/navigation/navTree";

interface IntermediatePageProps {
  /** Title of the page */
  title: string;
  /** Description of the section */
  description?: string;
  /** Icon component to display */
  icon?: React.ReactNode;
  /** Navigation node containing children */
  navNode: NavNode;
  /** Custom gradient for the header icon */
  headerGradient?: {
    from: string;
    to: string;
  };
}

/**
 * **IntermediatePage**
 *
 * Generic intermediate page component that automatically generates
 * child cards based on the navigation tree structure.
 *
 * Used for pages like HealthApp, PersonalDevelopmentApp, AdminPanelApp, etc.
 */
export const IntermediatePage: FC<IntermediatePageProps> = ({
  title,
  description,
  icon,
  navNode,
  headerGradient = { from: "from-primary-500", to: "to-primary-600" },
}) => {
  // Get children from navNode
  const children = navNode.children || [];

  // Define gradient presets for different types of apps
  const gradientPresets = {
    health: { from: "from-blue-500", to: "to-blue-600" },
    productivity: { from: "from-purple-500", to: "to-purple-600" },
    admin: { from: "from-orange-500", to: "to-orange-600" },
    tools: { from: "from-green-500", to: "to-green-600" },
    default: { from: "from-primary-500", to: "to-primary-600" },
  };

  // Helper function to determine gradient based on title/keywords
  const getGradient = (title: string): { from: string; to: string } => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("health") || lowerTitle.includes("oura")) return gradientPresets.health;
    if (lowerTitle.includes("productivity") || lowerTitle.includes("miracle"))
      return gradientPresets.productivity;
    if (
      lowerTitle.includes("admin") ||
      lowerTitle.includes("script") ||
      lowerTitle.includes("roadmap")
    )
      return gradientPresets.admin;
    if (lowerTitle.includes("tool") || lowerTitle.includes("time")) return gradientPresets.tools;
    return gradientPresets.default;
  };

  return (
    <div className="min-h-screen bg-background p-6 space-y-6 text-primary">
      <header className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {icon && (
            <div
              className={`w-12 h-12 bg-gradient-to-br ${headerGradient.from} ${headerGradient.to} rounded-lg flex items-center justify-center`}
            >
              {icon}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{title}</h1>
            {description && <p className="text-neutral-400 text-sm mt-1">{description}</p>}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {children.map((child) => {
          const gradient = getGradient(child.title);

          // If child has children, it's an intermediate level - show as coming soon for now
          if (child.children && child.children.length > 0) {
            return (
              <ComingSoonCard
                key={child.id}
                title={child.title}
                description={`${child.title} tools and features`}
                gradient={gradient}
                iconLetter={child.title.charAt(0).toUpperCase()}
              />
            );
          }

          // If child has a link, it's a leaf node - show as active card
          if (child.link) {
            return (
              <ChildCard
                key={child.id}
                title={child.title}
                description={`${child.title} dashboard and tools`}
                to={child.link}
                gradient={gradient}
                iconLetter={child.title.charAt(0).toUpperCase()}
                ctaText="Open Dashboard"
              />
            );
          }

          // Fallback for nodes without link or children
          return (
            <ComingSoonCard
              key={child.id}
              title={child.title}
              description={`${child.title} features coming soon`}
              gradient={gradient}
              iconLetter={child.title.charAt(0).toUpperCase()}
            />
          );
        })}

        {/* Add a "More Tools" placeholder if there are children */}
        {children.length > 0 && (
          <ComingSoonCard
            title="More Tools"
            description="Additional features and integrations will be available here."
            gradient={gradientPresets.default}
            iconLetter="+"
            message="Coming Soon"
          />
        )}
      </div>
    </div>
  );
};
